// ============================================================================
// COMMON API TYPES
// ============================================================================
// Shared types used across all API modules

/**
 * Standard API Response Wrapper
 * All API responses should follow this structure
 */
export interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message?: string;
  timestamp?: string;
}

/**
 * API Error Response
 */
export interface ApiError {
  success: false;
  error: {
    code: string;
    message: string;
    details?: Record<string, any>;
    stack?: string;
  };
  timestamp: string;
}

/**
 * Paginated Response
 * For endpoints that return paginated data
 */
export interface PaginatedResponse<T = any> {
  success: boolean;
  data: T[];
  pagination: PaginationMeta;
  message?: string;
}

/**
 * Pagination Metadata
 */
export interface PaginationMeta {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalItems: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

/**
 * Pagination Query Parameters
 */
export interface PaginationParams {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Search Query Parameters
 */
export interface SearchParams extends PaginationParams {
  query?: string;
  filters?: Record<string, any>;
}

/**
 * Date Range Filter
 */
export interface DateRange {
  startDate: string;
  endDate: string;
}

/**
 * Pagination Parameters (matches backend QueryParameters)
 */
export interface PaginationParams {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

/**
 * Generic Filter Parameters
 */
export interface FilterParams {
  status?: string | string[];
  createdAt?: DateRange;
  updatedAt?: DateRange;
  [key: string]: any;
}

/**
 * ID Types
 */
export type ID = string | number;

/**
 * Status Types
 */
export type Status = 'Active' | 'Inactive' | 'Draft' | 'Archived';

/**
 * Audit Fields
 * Common fields for tracking creation and modification
 */
export interface AuditFields {
  createdAt: string;
  createdBy: string;
  createdByName?: string;
  updatedAt?: string;
  updatedBy?: string;
  updatedByName?: string;
}

/**
 * Soft Delete Fields
 */
export interface SoftDeleteFields {
  isDeleted: boolean;
  deletedAt?: string;
  deletedBy?: string;
}

/**
 * Base Entity Interface
 * Common fields for all entities
 */
export interface BaseEntity extends AuditFields {
  id: ID;
  status: Status;
}

/**
 * File Upload Response
 */
export interface FileUploadResponse {
  success: boolean;
  data: {
    fileId: string;
    fileName: string;
    fileUrl: string;
    fileSize: number;
    mimeType: string;
  };
}

/**
 * Bulk Operation Response
 */
export interface BulkOperationResponse {
  success: boolean;
  data: {
    successCount: number;
    failureCount: number;
    errors?: Array<{
      id: ID;
      error: string;
    }>;
  };
}

/**
 * Transform API's PagedResult response into frontend PaginatedResponse.
 *
 * API returns:  { success, data: { items: T[], page, pageSize, totalCount, totalPages, hasPrevious, hasNext }, message }
 * Frontend expects: { success, data: T[], pagination: PaginationMeta, message }
 */
export function transformPaginatedResponse<T>(apiResponse: any): PaginatedResponse<T> {
  const pageData = apiResponse.data;
  return {
    success: apiResponse.success,
    data: pageData?.items || (Array.isArray(pageData) ? pageData : []),
    pagination: {
      currentPage: pageData?.page || 1,
      pageSize: pageData?.pageSize || 10,
      totalPages: pageData?.totalPages || 1,
      totalItems: pageData?.totalCount || 0,
      hasNextPage: pageData?.hasNext || false,
      hasPreviousPage: pageData?.hasPrevious || false,
    },
    message: apiResponse.message,
  };
}

/**
 * Validation Error
 */
export interface ValidationError {
  field: string;
  message: string;
  rule?: string;
}

/**
 * Validation Error Response
 */
export interface ValidationErrorResponse extends ApiError {
  error: {
    code: 'VALIDATION_ERROR';
    message: string;
    details: {
      errors: ValidationError[];
    };
  };
}

/**
 * Generic List Response (non-paginated)
 */
export interface ListResponse<T = any> {
  success: boolean;
  data: T[];
  totalCount: number;
  message?: string;
}

/**
 * Empty Response (for delete operations, etc.)
 */
export interface EmptyResponse {
  success: boolean;
  message?: string;
}
